// v0.3.0: Canvas renderer.
// v0.7.0: emotes, zoom.
// v0.8.0: side-view world, sit/climb poses, desk nameplates.
// v0.9.0: smooth cartoon art — vector chibi characters, indoor office-building tiles.
// v0.10.0: rotating-limb walk cycle, furniture that sits on desks, compact floors.
// v0.15.0: Ottsy — Ghibli-style otter characters (fur, eyes, headwear, top, bottom, mood).
// v0.17.0: padded sprite/tile canvases (nothing clips), two-segment limbs, chair-fitted sit,
//          platform blocks, renamable meeting rooms.
// v0.18.0: 3/4-view chairs + seated pose, pass-through platforms, jump = paws up, furniture
//          library (5 kinds x 50 styles) for build mode.
// v0.19.0: low chairs (sit without climbing), ledges drawn at the bottom of platform tiles,
//          3/4 jump pose, live camera feed above the head.
// v1.0.0: Gather-style TOP-DOWN world — floors, walls with height, y-sorted furniture, otters with
//         four facings (front / back / side), same vector art style.
import type { OfficeMap } from '../src/map';
import type { DeskMap, Dir, EmoteKind, Expr, EyeStyle, Facing, HatStyle, PlaceKind, PlayerState, RoomNames } from '../src/protocol';
import { DEFAULT_AVATAR, EMOTE_MS, JUMP_MS, TILE, MOVE_MIN_INTERVAL_MS } from '../src/protocol';

export interface RenderPlayer extends PlayerState {
  rx: number; // interpolated render position (tiles)
  ry: number;
  face: Facing;
  /** performance.now() of the last tile change — keeps the walk pose through the gaps between steps */
  movedAt?: number;
  speaking?: boolean;
  emote?: { kind: EmoteKind; start: number };
  /** v1.20.0: a Nearby chat message shown as a speech bubble over the head */
  say?: { text: string; start: number };
}

const SAY_MS = 7000;

export const ZOOM_MIN = 0.75;
export const ZOOM_MAX = 3;

const OUTLINE = 'rgba(58,38,28,0.9)';
// v1.4.0: environment art uses a lighter, tinted line so furniture reads soft instead of cartoon-harsh
const TOUT = 'rgba(78,60,48,0.42)';
let strokeDefault = OUTLINE;
const TILE_SCALE = 2;
// v0.17.0: the otter is authored in a 32x48 box, but hats, raised paws, the tail and the
// flop pose reach past it — so every sprite canvas carries a margin and is drawn offset.
const SPR_PAD_X = 10, SPR_PAD_Y = 18, SPR_W = 32 + SPR_PAD_X * 2, SPR_H = 48 + SPR_PAD_Y;
// same for furniture that pokes out of its tile (coffee machine, lamp, rug, desk lip)
const TILE_PAD = 32; // v1.5.0: drawn props can be ~2 tiles tall
/** how far (px) a seated otter is lifted so its bum lands on the seat cushion, per seat type */
const SEAT_LIFT: Record<string, number> = { chair: 3, sofa: 3, bench: 3 };
/** v1.16.0: room name plates are small signs beside the door */
const BANNER_FONT = 'bold 8px system-ui';
/** v1.14.0: otters are drawn at this share of their sprite size, so they sit in scale with the desks and chairs
 * (a standing otter ~1.2 tiles tall instead of 1.5) */
const AVATAR_SCALE = 0.8;
const WALK_FRAMES = 8;
const WALK_CYCLE_MS = 560;
/** v1.13.0: the drawn wave (art-31) swings between its two frames this often */
const WAVE_FRAME_MS = 260;


function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}

function ellipse(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number): void {
  ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2); ctx.closePath();
}

function fillStroke(ctx: CanvasRenderingContext2D, fill: string | CanvasGradient, stroke = strokeDefault, lw = 0.9): void {
  ctx.fillStyle = fill; ctx.fill();
  if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = lw; ctx.stroke(); }
}

function vgrad(ctx: CanvasRenderingContext2D, y0: number, y1: number, c0: string, c1: string): CanvasGradient {
  const g = ctx.createLinearGradient(0, y0, 0, y1); g.addColorStop(0, c0); g.addColorStop(1, c1); return g;
}

// v1.17.0: every tile, prop and otter comes from the drawn (ChatGPT) atlas; the procedural vector art is gone

export const KIND_LABEL: Record<PlaceKind, string> = { table: 'Tables', chair: 'Chairs', block: 'Obstacles', platform: 'Rugs', decor: 'Decor' };

/** Thumbnail for the build palette. */
export function drawFurnitureThumb(canvas: HTMLCanvasElement, kind: PlaceKind, style: number, face: Facing = 'down'): void {
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const s = Math.min(canvas.width, canvas.height) / (TILE + 20);
  ctx.setTransform(s, 0, 0, s, (canvas.width - TILE * s) / 2, (canvas.height - TILE * s) / 2 + 6 * s);
  const af = artForFurniture(kind, style, face);
  if (af) drawArtFurniture(ctx, af, kind, style);
}
/** v1.17.0: build-mode styles without a drawn piece are not offered */
export function furnitureHasArt(kind: PlaceKind, style: number): boolean { return !!artForFurniture(kind, style, 'down'); }

// ---------- Ottsy otters: drawn atlas frames composed with their top / headwear / mood layers ----------

export type Pose = 'idle' | 'sit' | 'handsup' | 'flat' | 'jump' | `walk${number}` | `wave${number}`;

interface Look {
  fur: string; top: string; topStyle: number; bottom: string; bottomStyle: number;
  hat: HatStyle; hatColor: string; eyes: EyeStyle; expr: Expr;
}

const EMOTE_EXPR: Record<EmoteKind, Expr> = { wave: 'wink', dance: 'happy', laugh: 'laugh', heart: 'heart', shock: 'shock', angry: 'angry', jump: 'happy' };
export function drawAvatarPreview(canvas: HTMLCanvasElement, avatar: PlayerState['avatar'], id: string): void {
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  drawPreviewBackdrop(ctx, canvas.width, canvas.height);
  const s = Math.min(canvas.width / SPR_W, canvas.height / SPR_H);
  ctx.setTransform(s, 0, 0, s, (canvas.width - SPR_W * s) / 2 + SPR_PAD_X * s, (canvas.height - SPR_H * s) / 2 + SPR_PAD_Y * s);
  const look = lookOf(id, avatar);
  const art = artOtter('idle', 'down', look, look.expr);
  if (art) { const px = ART?.px ?? 4; const w = art.c.width / px, h = art.c.height / px; ctx.drawImage(art.c, 16 - w / 2, 46 - h, w, h); }
}

/** v1.8.0: a slice of the office behind the avatar preview — plaster wall with its cap and baseboard, wood floor */
function drawPreviewBackdrop(ctx: CanvasRenderingContext2D, W: number, H: number): void {
  const wood = artFrame('floor:wood'), wall = artFrame('wall:plain');
  if (!ART || !wood || !wall) { ctx.fillStyle = '#cfe8fb'; ctx.fillRect(0, 0, W, H); return; }
  const bandH = Math.round(H * 0.42), t = Math.ceil(W / 3);
  const ix = wood.w * 0.08, iy = wood.h * 0.08;
  for (let y = bandH; y < H; y += t) for (let x = 0; x < W; x += t) ctx.drawImage(ART.img, wood.x + ix, wood.y + iy, wood.w - ix * 2, wood.h - iy * 2, x, y, t, t);
  const sx = wall.x + wall.w * WALL_IN[0], sw = wall.w * (WALL_IN[1] - WALL_IN[0]);
  ctx.drawImage(ART.img, sx, wall.y + wall.h * WALL_CAP[1], sw, wall.h * (WALL_BASE[0] - WALL_CAP[1]), 0, 0, W, bandH);
  const cap = Math.round(bandH * 0.11);
  ctx.drawImage(ART.img, sx, wall.y + wall.h * WALL_CAP[0], sw, wall.h * (WALL_CAP[1] - WALL_CAP[0]), 0, 0, W, cap);
  ctx.drawImage(ART.img, sx, wall.y + wall.h * WALL_BASE[0], sw, wall.h * (WALL_BASE[1] - WALL_BASE[0]), 0, bandH - cap, W, cap);
  const g = ctx.createLinearGradient(0, bandH, 0, bandH + 14); g.addColorStop(0, 'rgba(40,30,20,0.18)'); g.addColorStop(1, 'rgba(40,30,20,0)');
  ctx.fillStyle = g; ctx.fillRect(0, bandH, W, 14);
}

/** v1.8.7: an option tile is the otter itself with just that option changed — the head for a mood or a hat,
 * the chest for a top — cropped the same way on every tile, so the row reads as one set. false = no atlas, use text. */
export type ThumbKind = 'face' | 'fur' | 'hat' | 'top';
export function drawAvatarPartThumb(canvas: HTMLCanvasElement, kind: ThumbKind, key: string, avatar: PlayerState['avatar']): boolean {
  const base = artFrame('otter:idle:down');
  if (!ART || !base || base.hw === undefined || base.hx === undefined) return false;
  const me = lookOf('', avatar);
  const look: Look = kind === 'face' ? { ...me, hat: 'none', expr: key as Expr }
    : kind === 'fur' ? { ...me, fur: key, hat: 'none' }
    : kind === 'hat' ? { ...me, hat: key as HatStyle, expr: 'normal' }
    : { ...me, topStyle: parseInt(key, 10), hat: 'none', expr: 'normal' };
  const art = artOtter('idle', 'down', look, look.expr);
  if (!art) return false;
  const hw = base.hw, hx = base.hx + art.padX, headTop = art.pad + (base.hy ?? 0);
  // v1.9.2: a hat tile frames the whole drawn hat (a sun hat's brim is wider than the head)
  const hl = kind === 'hat' ? artFrame(`hatl:${key}:idle:down`) : null;
  const hatW = hl ? Math.max(hw * 1.4, 2 * Math.max(hx - (hl.ox ?? 0) - art.padX, (hl.ox ?? 0) + art.padX + hl.w - hx) + 8) : hw * 1.4;
  const hatTop = hl ? Math.min(headTop - hw * 0.45, art.pad + (hl.oy ?? 0) - 6) : headTop - hw * 0.45;
  // crop (source px): moods tight on the face, hats square with room above the head, tops a portrait of
  // head + torso (v1.8.9: a crop that started at the chin left half a face above every garment)
  const [sw, sh, top] = kind === 'face' || kind === 'fur' ? [hw * 1.08, hw * 1.08, headTop - hw * 0.04]
    : kind === 'hat' ? [hatW, hatW, hatTop]
    : [hw * 1.35, hw * 1.77, headTop - hw * 0.08];
  const ctx = canvas.getContext('2d')!;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(art.c, hx - sw / 2, top, sw, sh, 0, 0, canvas.width, canvas.height);
  return true;
}

function lookOf(_id: string, a: PlayerState['avatar']): Look {
  return {
    fur: a.fur ?? DEFAULT_AVATAR.fur,
    top: a.color || DEFAULT_AVATAR.color,
    topStyle: a.shirtStyle ?? DEFAULT_AVATAR.shirtStyle,
    bottom: a.pants ?? DEFAULT_AVATAR.pants,
    bottomStyle: a.pantsStyle ?? DEFAULT_AVATAR.pantsStyle,
    hat: a.hat ?? DEFAULT_AVATAR.hat,
    hatColor: a.hatColor ?? DEFAULT_AVATAR.hatColor,
    eyes: a.eyes ?? DEFAULT_AVATAR.eyes,
    expr: a.expr ?? DEFAULT_AVATAR.expr,
  };
}

// ---------- v1.5.0: drawn art atlas (ChatGPT-generated sheets sliced by tools/slice_art.py) ----------
// When public/art/atlas.{png,json} is present, otters, floors and many props come from it; anything
// the atlas doesn't have falls back to the procedural drawers above.

// v1.9.2: ox/oy = where a hat layer (hatl:*) sits in its bare frame; hd = this frame's head offset from the frame the layers were cut for
// v1.10.0: bd = a dressed frame's offset from its own bare frame (mood layers are cut on the bare frames)
interface ArtFrame { x: number; y: number; w: number; h: number; kind: string; ax?: number; hx?: number; hw?: number; hy?: number; ny?: number; bx?: number; bw?: number; ex?: number; ey?: number; es?: number; ea?: number; ox?: number; oy?: number; hd?: [number, number]; bd?: [number, number] }
let ART: { img: HTMLImageElement | HTMLCanvasElement; px: number; frames: Record<string, ArtFrame> } | null = null;

/** v1.13.1: a lossy AVIF copy of the atlas (~3.7 MB vs 11 MB, tools/encode_atlas.py); v1.13.2: plus atlas.fix, the exact
 * colour of every pixel the AVIF would put on (or near) the wrong side of the garment key test, painted back over it so
 * recolouring is identical to the lossless atlas. The lossless WebP (v1.8.6) when either can't be had. */
async function loadAtlasImage(): Promise<HTMLImageElement | HTMLCanvasElement> {
  const lossless = async (): Promise<HTMLImageElement> => { const w = new Image(); w.src = '/art/atlas.webp'; await w.decode(); return w; };
  const a = new Image(); a.src = '/art/atlas.avif';
  const fixRes = fetch('/art/atlas.fix').then((r) => (r.ok ? r.arrayBuffer() : null)).catch(() => null);
  try { await a.decode(); } catch { return lossless(); }
  const buf = await fixRes;
  if (!buf) return lossless(); // never run on an uncorrected AVIF
  const fix = new Uint32Array(buf); // (pixel index, RGBA) pairs, sorted by index
  const c = document.createElement('canvas'); c.width = a.naturalWidth; c.height = a.naturalHeight;
  const g = c.getContext('2d')!; g.drawImage(a, 0, 0);
  const W = c.width, BAND = 64;
  for (let i = 0; i < fix.length;) {
    // patch one 64-row band at a time (one read-back + write per band rather than per pixel)
    const y0 = Math.floor(fix[i] / W / BAND) * BAND, h = Math.min(BAND, c.height - y0);
    const im = g.getImageData(0, y0, W, h), d = im.data;
    for (; i < fix.length && fix[i] < (y0 + h) * W; i += 2) {
      const o = (fix[i] - y0 * W) * 4, v = fix[i + 1];
      d[o] = v & 255; d[o + 1] = (v >>> 8) & 255; d[o + 2] = (v >>> 16) & 255; d[o + 3] = v >>> 24;
    }
    g.putImageData(im, 0, y0);
  }
  return c;
}

export async function loadArt(): Promise<boolean> {
  try {
    const meta = (await (await fetch('/art/atlas.json')).json()) as { px: number; frames: Record<string, ArtFrame> };
    const img = await loadAtlasImage();
    ART = { img, px: meta.px, frames: meta.frames };
    return true;
  } catch { ART = null; return false; }
}
export function artFrame(name: string): ArtFrame | null { return ART?.frames[name] ?? null; }

/** draw an atlas frame so its bottom-centre lands on (cx, bottom), at world scale */
function drawArt(ctx: CanvasRenderingContext2D, f: ArtFrame, cx: number, bottom: number, scale = 1): void {
  if (!ART) return;
  const w = (f.w / ART.px) * scale, h = (f.h / ART.px) * scale;
  ctx.drawImage(ART.img, f.x, f.y, f.w, f.h, cx - w / 2, bottom - h, w, h);
}

/** base furniture / decor from the atlas, anchored like the procedural tiles (feet at y≈31) */
function drawArtProp(ctx: CanvasRenderingContext2D, f: ArtFrame, scale = 1): void { drawArt(ctx, f, 16, 31, scale); }

// v1.6.0: every map object type has a drawn sprite now (furniture + office sheets)
const ART_OBJECTS: Record<string, string> = {
  plant: 'obj:plant', tree: 'obj:tree', flowers: 'obj:flowers', rug: 'obj:rug', fountain: 'obj:fountain',
  desk: 'obj:desk', deskpc: 'obj:deskpc', desklamp: 'obj:desklamp', table: 'obj:table', tableround: 'obj:tableround',
  chair_d: 'obj:chair_d', chair_u: 'obj:chair_u', chair_l: 'obj:chair_l', chair_r: 'obj:chair_r', bench: 'obj:bench',
  sofa: 'obj:sofa', sofa_u: 'obj:sofa_u', bookshelf: 'obj:bookshelf', counter: 'obj:counter', coffee: 'obj:coffee',
  fridge: 'obj:fridge', cooler: 'obj:cooler', printer: 'obj:printer', whiteboard: 'obj:whiteboard', tv: 'obj:tv',
  reception: 'obj:reception', sign: 'obj:sign', locker: 'obj:locker', vending: 'obj:vending', lamp: 'obj:lamp',
  podium: 'obj:podium', meet: 'obj:meet',
  // v1.15.0: Set E6 cosy pieces; the wall ones hang on the wall face of the tile they're on (see WALL_DECOR)
  cushion: 'obj:cushion', sidetable: 'obj:sidetable', coatrack: 'obj:coatrack', basket: 'obj:basket', kelp: 'obj:kelp',
  pebbles: 'obj:pebbles', fishrug: 'obj:fishrug', plush: 'obj:plush', record: 'obj:record',
  shelf: 'wall:shelf', curtains: 'wall:curtains', lights: 'wall:lights', notice: 'wall:notice',
  // v1.18.2: the pond (art-48)
  ...Object.fromEntries(['lilypad', 'lilypad_s', 'reeds', 'cattails', 'koi', 'koi2', 'mossrock', 'stepstone', 'log', 'duck', 'lotus', 'islet',
    'frog', 'lilybud', 'jetty', 'lantern'].map((k) => [k, `obj:${k}`])),
};
/** v1.17.0: pieces wider than a tile, put on one tile of the map (deskfill, invisible and solid, holds the rest): w tiles
 *  wide, starting dx tiles from that tile -- a work desk is centred on its chair's column, a table starts at its tile */
const MULTI_TILE: Record<string, { art: string; w: number; dx: number }> = {
  workdesk: { art: 'obj:workdesk', w: 2, dx: -0.5 }, table2: { art: 'obj:table2', w: 2, dx: 0 }, table4: { art: 'obj:table4', w: 4, dx: 0 },
};
/** v1.15.0: wall-hung pieces: drawn centred this far (px) below the top of their wall tile's face (which spans -16..32) */
const WALL_DECOR: Record<string, number> = { shelf: 6, notice: 6, curtains: 6, lights: 3 };
function artForObject(type: string): ArtFrame | null { const n = ART_OBJECTS[type]; return n ? artFrame(n) : null; }

/** v1.6.0: sprites wider than a tile — a run of N same tiles shows one piece sliced per tile (ends + repeated middle).
 *  h squashes the sprite to a sensible height; `single` replaces a lone tile. */
const WIDE_OBJECTS: Record<string, { h?: number; single?: string }> = {
  sofa: { h: 38, single: 'obj:armchair' }, sofa_u: { h: 38, single: 'obj:armchair' }, reception: { h: 34 }, whiteboard: { h: 44 },
};
function drawArtSlice(ctx: CanvasRenderingContext2D, f: ArtFrame, si: number, slices: number, h?: number): void {
  if (!ART) return;
  const dh = h ?? f.h / ART.px;
  if (si < 0) { ctx.drawImage(ART.img, f.x, f.y, f.w, f.h, 0, 31 - dh, TILE, dh); return; } // whole piece into one tile
  const sw = f.w / slices;
  ctx.drawImage(ART.img, f.x + sw * si, f.y, sw, f.h, 0, 31 - dh, TILE, dh);
}

/** v1.6.0: build-mode styles -> atlas frames (shape = style/10 for furniture, style%10 for decor) */
const ART_STYLE: Record<PlaceKind, (string | null)[]> = {
  table: ['obj:table', 'obj:tableround', 'obj:deskpc', 'obj:lowtable', 'obj:desklamp'],
  chair: ['obj:chair', 'obj:chair_wood', 'obj:stool', 'obj:armchair', 'obj:beanbag'],
  block: ['block:0', 'obj:bookshelf', 'obj:locker', 'obj:vending', 'obj:fridge'],
  platform: [null, 'obj:rug', 'obj:rug_rect', null, null],
  decor: ['obj:plant', 'decor:1', 'decor:2', 'obj:lamp', 'decor:4', 'decor:5', 'decor:6', 'decor:7', 'decor:8', 'decor:9'],
};
const ART_SCALE: Record<string, number> = { 'obj:rug_rect': 0.5 };
interface ArtPick { f: ArtFrame; scale: number }
function artForFurniture(kind: PlaceKind, style: number, face: Facing): ArtPick | null {
  const shape = kind === 'decor' ? style % 10 : Math.floor(style / 10);
  let name = ART_STYLE[kind][shape] ?? null;
  if (name === 'obj:chair') name = `obj:chair_${face[0]}`;
  if (!name) return null;
  const f = artFrame(name);
  return f ? { f, scale: ART_SCALE[name] ?? 1 } : null;
}
/** a build-mode piece; palettes 1-9 hue-shift upholstery and rugs (wood and metal keep their colour) */
function drawArtFurniture(ctx: CanvasRenderingContext2D, pick: ArtPick, kind: PlaceKind, style: number): void {
  const pal = kind === 'decor' ? 0 : style % 10;
  const tint = pal > 0 && (kind === 'chair' || kind === 'platform') && 'filter' in ctx;
  if (tint) ctx.filter = `hue-rotate(${pal * 36}deg)`;
  drawArtProp(ctx, pick.f, pick.scale);
  if (tint) ctx.filter = 'none';
}


/** v1.6.0: walls from the drawn wall sheet. A wall tile spans y -16..32; the sprite is cut into cap / plaster / baseboard bands
 *  (and its outlined side margins are dropped) so neighbouring tiles join seamlessly; windows and pictures keep their own aspect. */
export type WallVariant = 'top' | 'face' | 'window' | 'glass' | 'glassface' | 'picture';
const WALL_IN = [0.07, 0.94], WALL_CAP = [0.02, 0.13], WALL_BASE = [0.865, 0.98]; // v1.18.0: the Set F walls (art-44)
function drawArtWall(ctx: CanvasRenderingContext2D, v: WallVariant): void {
  // v1.17.0: only the drawn wall pieces: cap / plaster / skirting bands cut from them (no fill colours)
  const plain = artFrame('wall:plain');
  if (!ART || !plain) return;
  const img = ART.img, T = TILE;
  // v1.18.1: the plain wall's bands come from its middle columns only (the piece darkens toward its own ends, which
  // showed as a seam on every tile), so every wall tile is identical and they join evenly
  const band = (f: ArtFrame, r: number[], dx: number, dy: number, dw: number, dh: number): void => {
    const [u0, u1] = f === plain ? [0.3, 0.7] : WALL_IN;
    ctx.drawImage(img, f.x + f.w * u0, f.y + f.h * r[0], f.w * (u1 - u0), f.h * (r[1] - r[0]), dx, dy, dw, dh);
  };
  const PLASTER = [WALL_CAP[1], WALL_BASE[0]];
  if (v === 'top') { // the top of a wall that runs away from the viewer: plaster, with the cap trim turned along each side
    band(plain, [0.47, 0.53], 0, -16, T, T + 16); // a thin middle slice: the plaster's top-to-bottom shading would show a seam each tile
    const sx = plain.x + plain.w * WALL_IN[0], sw = plain.w * (WALL_IN[1] - WALL_IN[0]), cy = plain.y + plain.h * WALL_CAP[0], ch = plain.h * (WALL_CAP[1] - WALL_CAP[0]);
    for (const x of [0, T - 4]) { ctx.save(); ctx.translate(x + 4, -16); ctx.rotate(Math.PI / 2); ctx.drawImage(img, sx, cy, sw, ch, 0, 0, T + 16, 4); ctx.restore(); }
    return;
  }
  const glass = v === 'glass' || v === 'glassface';
  const seg = (glass ? artFrame('wall:glass') : v === 'window' ? artFrame('wall:window') : v === 'picture' ? artFrame('wall:picture') : plain) ?? plain;
  band(plain, WALL_CAP, 0, -16, T, 7);
  band(glass ? seg : plain, PLASTER, 0, -9, T, 34);
  band(plain, WALL_BASE, 0, 25, T, 7);
  if (seg !== plain && !glass) { // a window / picture (its own plaster cut away by the slicer) keeps its proportions, centred
    const my = seg.y + seg.h * WALL_CAP[1], mh = seg.h * (WALL_BASE[0] - WALL_CAP[1]);
    const msx = seg.x + seg.w * WALL_IN[0], msw = seg.w * (WALL_IN[1] - WALL_IN[0]);
    const dh = mh * (T / msw);
    ctx.drawImage(img, msx, my, msw, mh, 0, -9 + (34 - dh) / 2, T, dh);
  }
}
const FUR_HEX = (hex: string): [number, number, number] => { const n = parseInt(hex.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };

/** copy a frame into a fresh canvas (atlas resolution) */
function frameCanvas(f: ArtFrame, padTop = 0): HTMLCanvasElement {
  const c = document.createElement('canvas'); c.width = f.w; c.height = f.h + padTop;
  const ctx = c.getContext('2d')!;
  if (ART) ctx.drawImage(ART.img, f.x, f.y, f.w, f.h, 0, padTop, f.w, f.h);
  return c;
}

/** v1.8.5: the dressed sheets paint garment fabric in magenta (#FF00FF, shaded in lighter/darker magenta) */
const isKey = (r: number, g: number, b: number): boolean => r > g + 70 && b > g + 70 && Math.abs(r - b) < 90;

/** recolour the grey base fur: dark pixels (eyes, nose, line) and cream pixels (belly, muzzle) stay.
 * With `top`, magenta key pixels become that colour (shade and highlight kept); without it they are left alone. */
function tintFur(c: HTMLCanvasElement, fur: string, top?: string, keepColour = false): void {
  const ctx = c.getContext('2d')!; const im = ctx.getImageData(0, 0, c.width, c.height); const d = im.data;
  const [fr, fg, fb] = FUR_HEX(fur);
  const [tr, tg, tb] = top ? FUR_HEX(top) : [0, 0, 0];
  for (let i = 0; i < d.length; i += 4) {
    const a = d[i + 3]; if (!a) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    if (isKey(r, g, b)) {
      if (!top) continue;
      const m = Math.max(r, b), v = m / 255, w = g / m; // v: how dark the shade, w: how far toward white
      d[i] = tr * v * (1 - w) + 255 * w; d[i + 1] = tg * v * (1 - w) + 255 * w; d[i + 2] = tb * v * (1 - w) + 255 * w;
      continue;
    }
    const lum = (0.3 * r + 0.59 * g + 0.11 * b) / 255;
    if (lum < 0.2 || lum > 0.74) continue;           // outline/eyes or belly/cream — untouched
    // v1.10.0: a mood's own colours (red heart eyes, pink tongue) are not fur
    if (keepColour) { const mx = Math.max(r, g, b); if (mx && (mx - Math.min(r, g, b)) / mx > 0.42) continue; }
    const k = lum / 0.52;                              // base fur sits around 0.52
    d[i] = Math.min(255, fr * k); d[i + 1] = Math.min(255, fg * k); d[i + 2] = Math.min(255, fb * k);
  }
  ctx.putImageData(im, 0, 0);
}

/** a white garment / hat tinted to a colour (keeps its shading), drawn into a box */
function drawTinted(ctx: CanvasRenderingContext2D, f: ArtFrame, color: string, x: number, y: number, w: number, h: number): void {
  if (!ART) return;
  const t = document.createElement('canvas'); t.width = f.w; t.height = f.h;
  const tc = t.getContext('2d')!;
  tc.drawImage(ART.img, f.x, f.y, f.w, f.h, 0, 0, f.w, f.h);
  tc.globalCompositeOperation = 'multiply'; tc.fillStyle = color; tc.fillRect(0, 0, f.w, f.h);
  tc.globalCompositeOperation = 'destination-in'; tc.drawImage(ART.img, f.x, f.y, f.w, f.h, 0, 0, f.w, f.h);
  ctx.drawImage(t, x, y, w, h);
}

/**
 * Assemble a drawn otter: base frame (fur-tinted) + top + hat + expression. Overlays are only
 * placed on frames that face the viewer (the sheets were drawn front-on). Returns a canvas at
 * atlas resolution whose bottom-centre is the feet; `pad` extra pixels were added above the
 * head so hats never clip.
 */
/** v1.8.1: how each hat sits on the measured head */
const HAT_FIT: Record<string, [number, number, number]> = {
  cap: [1.0, 0.02, -0.5], beanie: [0.96, 0, -0.58], flower: [0.42, 0.3, -0.4], bow: [0.42, -0.32, -0.4],
  // v1.9.1: headphones sit ON the ears (top corners of the head) with the band over the crown — at 0.92 of the
  // head the cups landed over the eyes, since this art's cups are large relative to its band
  headphones: [0.8, 0, -0.44], crown: [0.5, 0, -0.78], leaf: [0.4, 0.06, -0.85], sunhat: [1.15, 0, -0.6],
};
/** v1.8.5: the garment lifted off a dressed idle frame (key pixels plus a 3px rim for the outline and details),
 * untinted, so the walking / sitting / jumping frames can wear the same garment shape */
const cutoutCache = new Map<string, HTMLCanvasElement | null>();
function dressedCutout(view: string, topStyle: number): { c: HTMLCanvasElement; f: ArtFrame } | null {
  const f = artFrame(`otter:idle:${view}:top${topStyle}`);
  if (!f) return null;
  const key = `${view}|${topStyle}`;
  let c = cutoutCache.get(key);
  if (c === undefined) {
    c = frameCanvas(f);
    const ctx = c.getContext('2d')!; const im = ctx.getImageData(0, 0, c.width, c.height); const d = im.data;
    const W = c.width, H = c.height, R = 3;
    const keep = new Uint8Array(W * H);
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const i = (y * W + x) * 4;
      if (d[i + 3] > 24 && isKey(d[i], d[i + 1], d[i + 2])) {
        for (let yy = Math.max(0, y - R); yy <= Math.min(H - 1, y + R); yy++)
          for (let xx = Math.max(0, x - R); xx <= Math.min(W - 1, x + R); xx++) keep[yy * W + xx] = 1;
      }
    }
    let n = 0;
    for (let p = 0; p < W * H; p++) { if (!keep[p]) d[p * 4 + 3] = 0; else n++; }
    if (n < 50) c = null; else ctx.putImageData(im, 0, 0);
    cutoutCache.set(key, c);
  }
  return c ? { c, f } : null;
}

/** v1.10.0: square dilate (grow) / erode (shrink) of a 0/1 mask, separable */
function morph(m: Uint8Array, W: number, H: number, r: number, grow: boolean): Uint8Array {
  const tmp = new Uint8Array(W * H), out = new Uint8Array(W * H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    let v = grow ? 0 : 1;
    for (let k = -r; k <= r; k++) { const xx = x + k; const s = xx < 0 || xx >= W ? (grow ? 0 : 1) : m[y * W + xx]; if (grow ? s : !s) { v = grow ? 1 : 0; break; } }
    tmp[y * W + x] = v;
  }
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    let v = grow ? 0 : 1;
    for (let k = -r; k <= r; k++) { const yy = y + k; const s = yy < 0 || yy >= H ? (grow ? 0 : 1) : tmp[yy * W + x]; if (grow ? s : !s) { v = grow ? 1 : 0; break; } }
    out[y * W + x] = v;
  }
  return out;
}

/** v1.9.3: a copy of the (tinted) otter canvas keeping only the garment — the frame's magenta key pixels, grown 2px
 * to take the garment's outline — so it can be laid back over anything drawn on top of it */
function garmentOnly(c: HTMLCanvasElement, f: ArtFrame, pad: number): HTMLCanvasElement {
  const src = frameCanvas(f).getContext('2d')!.getImageData(0, 0, f.w, f.h).data;
  const out = document.createElement('canvas'); out.width = c.width; out.height = c.height;
  const o = out.getContext('2d')!; o.drawImage(c, 0, 0);
  const im = o.getImageData(0, 0, c.width, c.height); const d = im.data;
  const W = f.w, H = f.h;
  let keep = new Uint8Array(W * H);
  for (let p = 0; p < W * H; p++) { const i = p * 4; if (src[i + 3] > 24 && isKey(src[i], src[i + 1], src[i + 2])) keep[p] = 1; }
  // v1.10.0: close (grow 5, shrink 5) so buttons / pockets / stitching inside the fabric count as garment, then grow 2
  // for the outline. Pixels outside the otter never count.
  keep = morph(morph(keep, W, H, 5, true), W, H, 5, false).map((v, p) => v | keep[p]);
  keep = morph(keep, W, H, 2, true).map((v, p) => (src[p * 4 + 3] > 0 ? v : 0));
  for (let y = 0; y < out.height; y++) for (let x = 0; x < out.width; x++) {
    const fy = y - pad; if (fy < 0 || fy >= H || x >= W || !keep[fy * W + x]) d[(y * out.width + x) * 4 + 3] = 0;
  }
  o.putImageData(im, 0, 0);
  return out;
}

/** v1.8.4: garment placement on the frames whose torso can't be measured — width and x offset as a share of the
 * head, garment height and hip fade as a share of the chin-to-feet height, tilt in radians */
const POSE_FIT: Partial<Record<Pose, { bw: number; dx: number; h?: number; hip?: number; tilt?: number }>> = {
  jump: { bw: 0.9, dx: -0.08, h: 0.8, hip: 0.8, tilt: 0.45 },
  handsup: { bw: 0.98, dx: 0.02 },
};
/** v1.11.0: hands up / lying flat / jumping have back and side frames now (art-23). A look switches to them only once
 * every piece it wears is drawn for that view too (dressed top, hat layer, mood face); until then it keeps the front
 * frame, as before, rather than mixing a turned body with a pasted front-on piece. */
function turnedPose(pose: Pose, f: 'up' | 'right', look: Look, expr: Expr): boolean {
  const n = `${pose}:${f}`;
  return !!artFrame(`otter:${n}`)
    && (look.topStyle === 0 || !!artFrame(`otter:${n}:top${look.topStyle}`))
    && (look.hat === 'none' || !!artFrame(`hatl:${look.hat}:${n}`))
    && (expr === 'normal' || f === 'up' || !!artFrame(`mood:${expr}:${n}`));
}

type ArtOtter = { c: HTMLCanvasElement; pad: number; ax?: number; padX: number; turned: boolean };
/** v1.18.2: the otter sheets were drawn with a dark, furry outline and a cooler tone than the (later) furniture sheets,
 * which made the otters look pasted onto the office. Each composed otter gets the furniture's edge: its silhouette band
 * (4 atlas px) is smoothed and its dark line warmed to the furniture's mid brown, with a slight warm grade overall.
 * The inside (face, eyes, fur, garment) is untouched. */
function harmonise(c: HTMLCanvasElement): void {
  const g = c.getContext('2d')!, W = c.width, H = c.height;
  const im = g.getImageData(0, 0, W, H), d = im.data, n = W * H, BAND = 4;
  // distance (in px, up to BAND+1) from each opaque pixel to the nearest see-through one
  const dist = new Uint8Array(n);
  for (let i = 0; i < n; i++) dist[i] = d[i * 4 + 3] > 24 ? BAND + 1 : 0;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x; if (!dist[i]) continue;
    const up = y ? dist[i - W] : 0, lf = x ? dist[i - 1] : 0;
    dist[i] = Math.min(dist[i], up + 1, lf + 1);
  }
  for (let y = H - 1; y >= 0; y--) for (let x = W - 1; x >= 0; x--) {
    const i = y * W + x; if (!dist[i]) continue;
    const dn = y < H - 1 ? dist[i + W] : 0, rt = x < W - 1 ? dist[i + 1] : 0;
    dist[i] = Math.min(dist[i], dn + 1, rt + 1);
  }
  const src = new Uint8ClampedArray(d);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x, o = i * 4;
    const a = src[o + 3]; if (!a) continue;
    let r = src[o], gg = src[o + 1], b = src[o + 2], na = a;
    if (dist[i] <= BAND) {
      // the edge band: a 3x3 average (premultiplied, so the see-through side doesn't darken it)...
      let sr = 0, sg = 0, sb = 0, sa = 0, cnt = 0;
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        const xx = x + dx, yy = y + dy; if (xx < 0 || yy < 0 || xx >= W || yy >= H) continue;
        const q = (yy * W + xx) * 4, qa = src[q + 3];
        sr += src[q] * qa; sg += src[q + 1] * qa; sb += src[q + 2] * qa; sa += qa; cnt++;
      }
      if (sa) { r = sr / sa; gg = sg / sa; b = sb / sa; }
      na = sa / cnt;
      // ...and a dark line drawn toward the furniture's warm mid-brown outline
      const lum = 0.3 * r + 0.59 * gg + 0.11 * b, k = Math.max(0, Math.min(1, (110 - lum) / 60)) * 0.6;
      r += (128 - r) * k; gg += (88 - gg) * k; b += (58 - b) * k;
    }
    d[o] = r * 1.03; d[o + 1] = gg; d[o + 2] = b * 0.95; d[o + 3] = na;
  }
  g.putImageData(im, 0, 0);
}

function artOtter(pose: Pose, facing: Facing, look: Look, expr: Expr): ArtOtter | null {
  const out = artOtterRaw(pose, facing, look, expr);
  if (out) harmonise(out.c);
  return out;
}

function artOtterRaw(pose: Pose, facing: Facing, look: Look, expr: Expr): ArtOtter | null {
  if (!ART) return null;
  const f = facing === 'left' ? 'right' : facing;
  const walk = pose.startsWith('walk') ? Math.floor(parseInt(pose.slice(4), 10) / 2) % 4 : -1;
  let name: string;
  if (walk >= 0) name = `otter:walk${walk}:${f}`;
  else if (pose === 'idle' || pose === 'sit') name = `otter:${pose}:${f}`;
  else if (pose.startsWith('wave')) name = artFrame(`otter:${pose}:${f}`) ? `otter:${pose}:${f}` : `otter:idle:${f}`; // v1.13.0
  else if (f !== 'down' && turnedPose(pose, f, look, expr)) name = `otter:${pose}:${f}`;
  else name = `otter:${pose}:down`;
  const turned = !name.endsWith(':down') && (pose === 'handsup' || pose === 'flat' || pose === 'jump');
  // v1.8.5: standing still, the otter wears the garment drawn on it by the dressed turnaround sheets;
  // v1.8.6: so does every other pose (dressed walk cycles + sit / hands-up / flat / jump sheets), the lifted
  // cutout below only steps in for a frame that has no drawn dressed version
  const dressed = look.topStyle > 0 ? artFrame(`${name}:top${look.topStyle}`) : null;
  const base = dressed ?? artFrame(name) ?? artFrame(`otter:idle:${f}`);
  if (!base) return null;
  // v1.8.2: hats and garments go on every view (they used to vanish the moment you turned sideways);
  // the expression only exists front-on. Back views mirror the pieces so a bow / cap brim swaps sides.
  const view: 'front' | 'side' | 'back' = name.endsWith(':right') ? 'side' : name.endsWith(':up') ? 'back' : 'front';
  // v1.9.2: the hat as drawn ON this pose by the hat sheets — a layer registered to the bare frame by the slicer, so it
  // lands where the artist put it (no per-hat offsets). Walk frames wear the standing layer of their view, shifted by
  // the measured head offset; dressed frames add theirs (their redrawn otter sits a few px off the bare one).
  const layer = look.hat !== 'none' ? artFrame(`hatl:${look.hat}:${walk >= 0 ? `idle:${f}` : name.slice(6)}`) : null;
  const lx = layer ? (layer.ox ?? 0) + (base.hd?.[0] ?? 0) : 0, ly = layer ? (layer.oy ?? 0) + (base.hd?.[1] ?? 0) : 0;
  const pad = Math.max(Math.round(base.h * 0.22), layer ? 2 - ly : 0);
  const c = frameCanvas(base, pad);
  tintFur(c, look.fur, dressed ? look.top : undefined);
  const wear = (): ArtOtter => {
    if (!layer) return { c, pad, ax: base.ax, padX: 0, turned };
    const t = frameCanvas(layer); tintFur(t, look.fur, look.hatColor);
    // a wide brim can poke past the frame: widen both sides equally so the body stays centred
    const padX = Math.max(0, -lx, lx + layer.w - c.width);
    let out = c;
    if (padX > 0) { out = document.createElement('canvas'); out.width = c.width + padX * 2; out.height = c.height; out.getContext('2d')!.drawImage(c, padX, 0); }
    out.getContext('2d')!.drawImage(t, lx + padX, ly + pad);
    return { c: out, pad, ax: base.ax !== undefined ? base.ax + padX : undefined, padX, turned };
  };
  // v1.10.0: the mood as drawn ON this very frame by the mood sheets (front, side, walking, sitting, hands up, lying
  // flat) — cut on the bare frame by the slicer, so it sits on the head exactly; a dressed frame adds its offset from
  // the bare one, and its garment is laid back on top. No layer (back views, jumping) -> the old pasted face below.
  const moodL = expr !== 'normal' ? artFrame(`mood:${expr}:${name.slice(6)}`) : null;
  if (moodL) {
    const keep = dressed ? garmentOnly(c, base, pad) : null;
    const t = frameCanvas(moodL); tintFur(t, look.fur, undefined, true);
    const g0 = c.getContext('2d')!;
    g0.drawImage(t, (moodL.ox ?? 0) + (base.bd?.[0] ?? 0), (moodL.oy ?? 0) + (base.bd?.[1] ?? 0) + pad);
    if (keep) g0.drawImage(keep, 0, 0);
  }
  if (pose === 'flat') return wear();
  const ctx = c.getContext('2d')!;
  const W = base.w, H = base.h, top = pad;
  const sitting = pose === 'sit', jumping = pose === 'jump';
  const mirrored = (g: CanvasRenderingContext2D, x: number, w: number, draw: (x: number) => void): void => {
    if (view !== 'back') { draw(x); return; }
    g.save(); g.translate(x * 2 + w, 0); g.scale(-1, 1); draw(x); g.restore();
  };
  // v1.8.3: a garment is clipped to the otter's own silhouette so sleeves end where the arms end
  // and nothing hangs outside the body — it reads as worn instead of pasted on
  const masked = (hipY: number, draw: (g: CanvasRenderingContext2D) => void): void => {
    const t = document.createElement('canvas'); t.width = c.width; t.height = c.height;
    const g = t.getContext('2d')!; draw(g);
    g.globalCompositeOperation = 'destination-in'; g.drawImage(ART!.img, base.x, base.y, base.w, base.h, 0, top, base.w, base.h);
    // and fades out at the hips so a hem never shows between the legs
    const fade = g.createLinearGradient(0, hipY - 14, 0, hipY);
    fade.addColorStop(0, 'rgba(0,0,0,1)'); fade.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = fade; g.fillRect(0, 0, t.width, t.height);
    ctx.drawImage(t, 0, 0);
  };
  // v1.8.1: the head as measured by the slicer (top band of the frame); the old guess used the frame width, tail included
  const measured = base.hw !== undefined && base.hx !== undefined; // v1.8.4: the slicer now keeps raised paws out of the head box
  const headW = measured ? base.hw! : W * (sitting ? 0.74 : 0.66);
  const headCx = measured ? base.hx! : W / 2 + (jumping ? W * 0.04 : 0);
  const headTop = top + (measured ? base.hy ?? 0 : H * (sitting ? 0.03 : 0.02));
  // v1.8.3: head height = top of head to the measured chin; the torso box sizes the garment
  const chinY = base.ny !== undefined ? top + base.ny : headTop + headW * 0.92; // the chin row is reliable even with paws up
  const bodyH = top + H - chinY; // chin to feet
  const cut = !dressed && look.topStyle > 0 ? dressedCutout(f, look.topStyle) : null;
  if (cut) {
    // v1.8.5: moving / sitting frames wear the garment lifted off the dressed idle frame of the same view, lined up
    // on the chin and head centre (same body, so sleeves and hem already follow it), clipped to this frame's outline
    const t = document.createElement('canvas'); t.width = cut.c.width; t.height = cut.c.height;
    t.getContext('2d')!.drawImage(cut.c, 0, 0); tintFur(t, look.fur, look.top);
    const dx = (base.hx ?? base.ax ?? 0) - (cut.f.hx ?? cut.f.ax ?? 0), dy = (base.ny ?? 0) - (cut.f.ny ?? 0);
    const fit = POSE_FIT[pose];
    masked(c.height + 20, (gc) => {
      gc.save();
      if (fit?.tilt) { gc.translate(headCx + headW * fit.dx, chinY); gc.rotate(fit.tilt); gc.translate(-(headCx + headW * fit.dx), -chinY); }
      gc.drawImage(t, dx, top + dy);
      gc.restore();
    });
  } else if (look.topStyle > 0 && !dressed) { // v1.9.3: never on a frame that already has the garment drawn on (it was stamped twice since v1.8.5)
    const g = artFrame(`top:${look.topStyle}`);
    if (g) {
      // v1.8.4: the two acrobatic frames can't be measured from their belly rows (an arm or a leg crosses them):
      // the torso is taken from the head instead, and the jump garment leans with the curled body
      const fit = POSE_FIT[pose];
      const torso = !fit && measured && base.bw !== undefined && base.bx !== undefined;
      const bw = fit ? headW * fit.bw : torso ? base.bw! : headW * (view === 'side' ? 0.86 : 1.0);
      const bcx = fit ? headCx + headW * fit.dx : torso ? base.bx! : view === 'side' ? (base.ax ?? headCx) + headW * 0.06 : headCx;
      // crouched frames (jump) have a short body: the garment is squashed to fit rather than hanging to the feet
      const gw = bw * 1.02, gh = Math.min(gw * (g.h / g.w), bodyH * (fit?.h ?? 0.68));
      const gy = chinY - gh * 0.08; // the collar tucks just under the chin
      masked(chinY + bodyH * (fit?.hip ?? 0.64), (gc) => {
        gc.save();
        if (fit?.tilt) { gc.translate(bcx, chinY); gc.rotate(fit.tilt); gc.translate(-bcx, -chinY); }
        mirrored(gc, bcx - gw / 2, gw, (x) => {
          drawTinted(gc, g, look.top, x, gy, gw, gh);
          // v1.8.4: seen from behind, the garment is a plain back — the buttons, pockets and bib of the
          // front-on art are washed out under a flat coat of its own colour
          if (view === 'back') {
            const t = document.createElement('canvas'); t.width = g.w; t.height = g.h;
            const tc = t.getContext('2d')!;
            tc.fillStyle = look.top; tc.fillRect(0, 0, g.w, g.h);
            tc.globalCompositeOperation = 'destination-in'; tc.drawImage(ART!.img, g.x, g.y, g.w, g.h, 0, 0, g.w, g.h);
            gc.globalAlpha = 0.62; gc.drawImage(t, x, gy, gw, gh); gc.globalAlpha = 1;
          }
        });
        gc.restore();
      });
    }
  }
  // v1.9.3: the mood's feathered edge must not wash over a drawn collar — the garment (key pixels + its outline,
  // from the untinted frame) is put back on top of the face afterwards
  const keepGarment = !moodL && dressed && expr !== 'normal' && view === 'front' ? garmentOnly(c, base, pad) : null;
  if (!moodL && expr !== 'normal' && view === 'front') {
    // v1.7.0: the expression is tinted like the body and feathered to the inside of the head, so only the
    // features land on the base face (no second head outline, no grey patch on a coloured otter)
    const fc = artFrame(`face:${expr}`);
    if (fc) {
      // v1.8.4: sized to the head but anchored on the measured pupils (the face sheet's sit at 50% / 40% of
      // the frame) and tilted with the head, so the mood lands on the eyes in every pose — sitting, jumping,
      // paws up — instead of sliding down the face. (Sizing from the eye spacing made the hands-up face wider
      // than the head and buried the raised paws.)
      const eyes = base.ex !== undefined && base.ey !== undefined;
      const fw = headW, fh = fw * (fc.h / fc.w);
      const ax = eyes ? base.ex! : headCx, ay = eyes ? top + base.ey! : headTop + fh * 0.4;
      const fcv = frameCanvas(fc); tintFur(fcv, look.fur);
      const fctx = fcv.getContext('2d')!;
      const g = fctx.createRadialGradient(fcv.width / 2, fcv.height * 0.52, 0, fcv.width / 2, fcv.height * 0.52, fcv.width * 0.46);
      g.addColorStop(0, 'rgba(0,0,0,1)'); g.addColorStop(0.78, 'rgba(0,0,0,1)'); g.addColorStop(1, 'rgba(0,0,0,0)');
      fctx.globalCompositeOperation = 'destination-in'; fctx.fillStyle = g; fctx.fillRect(0, 0, fcv.width, fcv.height);
      ctx.save(); ctx.translate(ax, ay); ctx.rotate(eyes ? base.ea! : 0);
      ctx.drawImage(fcv, -fw / 2, -fh * 0.4, fw, fh);
      ctx.restore();
    }
  }
  if (keepGarment) ctx.drawImage(keepGarment, 0, 0);
  if (layer) return wear();
  if (look.hat !== 'none') { // fallback for an atlas without hat layers: the v1.8 flat hat, placed by HAT_FIT
    const h = artFrame(`hat:${look.hat}`);
    if (h) {
      // [width as a share of the head, x offset as a share of the head, y offset as a share of the hat's own height]
      const [kw, kx, ky] = HAT_FIT[look.hat] ?? [1, 0, -0.5];
      // side view: the ear pieces (flower / bow) move to the front of the head
      const hw = headW * kw, hh = hw * (h.h / h.w); // the measured side head is already narrower
      const hx = headCx + headW * (view === 'side' ? Math.abs(kx) * 0.5 + 0.03 : kx), hy = headTop + hh * ky;
      const natural = look.hat === 'headphones';
      mirrored(ctx, hx - hw / 2, hw, (x) => {
        if (natural) ctx.drawImage(ART!.img, h.x, h.y, h.w, h.h, x, hy, hw, hh);
        else drawTinted(ctx, h, look.hatColor, x, hy, hw, hh);
      });
    }
  }
  return { c, pad, ax: base.ax, padX: 0, turned };
}

function drawArtTile(ctx: CanvasRenderingContext2D, f: ArtFrame, x: number, y: number): void {
  // v1.18.0: the floor swatches are made seamless by the slicer; a 1px inset only keeps the next atlas frame from bleeding in
  const ix = 1, iy = 1;
  if (ART) ctx.drawImage(ART.img, f.x + ix, f.y + iy, f.w - ix * 2, f.h - iy * 2, x * TILE, y * TILE, TILE, TILE);
}

// ---------- renderer (v1.0.0 top-down: static floor + y-sorted props and players) ----------

interface Prop { x: number; y: number; img: HTMLCanvasElement; seat?: boolean; w?: number; open?: HTMLCanvasElement }
/** v1.4.0: a soft contact shadow so props sit on the floor instead of floating */
function propShadow(ctx: CanvasRenderingContext2D, cx = 16, r = 15): void {
  const g = ctx.createRadialGradient(cx, 29, 2, cx, 29, r);
  g.addColorStop(0, 'rgba(40,30,20,0.22)'); g.addColorStop(1, 'rgba(40,30,20,0)');
  ctx.fillStyle = g; ctx.save(); ctx.scale(1, 0.32); ctx.beginPath(); ctx.arc(cx, 29 / 0.32, r, 0, Math.PI * 2); ctx.fill(); ctx.restore();
}
const SOLID_PROP_TYPES = new Set(['wall', 'window', 'glass']);
/** objects that lie flat on the floor (painted into the static layer, never occlude anyone) */
const FLAT_OBJECTS = new Set(['rug', 'flowers', 'fishrug', 'lilypad', 'lilypad_s', 'koi', 'koi2', 'stepstone', 'log', 'lilybud']);
/** v1.18.2: until the pond sheet (art-47) is in, its water shows the blue carpet and its bank the stepping-stone path */
const FLOOR_STANDIN: Record<string, string> = { water: 'carpet', water2: 'carpet', shore: 'path', shore2: 'path', shore3: 'path' };

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private staticLayer: HTMLCanvasElement;
  private tileCache = new Map<string, HTMLCanvasElement>();
  private dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
  private roomNames: RoomNames = {};
  private furnCache = new Map<string, HTMLCanvasElement>();
  /** props grouped by row, each row sorted by x */
  private rows: Prop[][] = [];
  /** v0.19.0: live camera element for a player (drawn in the little screen above their head) */
  videoFor: ((id: string) => HTMLVideoElement | null) | null = null;
  /** v0.18.0: build-mode preview drawn at the hovered tile */
  ghost: { kind: PlaceKind; style: number; face: Facing; x: number; y: number; ok: boolean } | null = null;
  camX = 0; camY = 0;
  zoom = 1.5;

  constructor(private canvas: HTMLCanvasElement, private map: OfficeMap) {
    this.ctx = canvas.getContext('2d')!;
    this.staticLayer = this.buildStatic();
    this.buildProps();
    this.resize();
    window.addEventListener('resize', () => this.resize());
  }

  resize(): void {
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = Math.round(rect.width * this.dpr);
    this.canvas.height = Math.round(rect.height * this.dpr);
  }

  setZoom(z: number): void { this.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z)); }

  /** v0.17.0: apply renamed rooms and repaint the banners. */
  setRoomNames(rooms: RoomNames): void {
    this.roomNames = { ...rooms };
    this.staticLayer = this.buildStatic();
  }

  /** v0.18.0: the map's placements changed — repaint. */
  rebuildStatic(): void { this.staticLayer = this.buildStatic(); this.buildProps(); }

  private padded(key: string, draw: (ctx: CanvasRenderingContext2D) => void): HTMLCanvasElement {
    let c = this.tileCache.get(key);
    if (c) return c;
    c = document.createElement('canvas');
    c.width = (TILE + TILE_PAD * 2) * TILE_SCALE; c.height = (TILE + TILE_PAD * 2) * TILE_SCALE;
    const ctx = c.getContext('2d')!;
    ctx.scale(TILE_SCALE, TILE_SCALE);
    ctx.translate(TILE_PAD, TILE_PAD);
    if (key.includes('obj:') && !/:(rug|flowers|fishrug|cushion|lilypad|lilypad_s|koi|koi2|stepstone|log|lilybud|duck|lotus|frog|islet)$/.test(key)) propShadow(ctx);
    strokeDefault = key.startsWith('floor:') ? OUTLINE : TOUT;
    draw(ctx);
    strokeDefault = OUTLINE;
    this.tileCache.set(key, c);
    return c;
  }

  /** v1.6.0: a wall tile from the drawn sheet, else procedural */
  private wallTile(v: WallVariant): HTMLCanvasElement {
    return this.padded(`wall:${v}`, (cx) => drawArtWall(cx, v));
  }

  /** v1.17.0: a piece MULTI_TILE[type].w tiles wide (its canvas is that wide plus the usual padding), `lift` px up */
  private multiTileObject(type: string, lift: number, behind = false, variant = ''): HTMLCanvasElement {
    const key = `multi:${type}:${lift}:${+behind}${variant}`, mt = MULTI_TILE[type];
    let c = this.tileCache.get(key);
    if (c) return c;
    c = document.createElement('canvas');
    c.width = (mt.w * TILE + TILE_PAD * 2) * TILE_SCALE; c.height = (TILE + TILE_PAD * 2) * TILE_SCALE;
    const cx = c.getContext('2d')!;
    cx.scale(TILE_SCALE, TILE_SCALE); cx.translate(TILE_PAD, TILE_PAD);
    // v1.18.1: a work desk whose chair is behind it shows its monitor from the back (it faces the chair)
    // v1.20.1: (hand-drawn now; some have a leaf sticker on the back)
    const af = (behind ? artFrame(`${mt.art}_back${variant}`) ?? artFrame(`${mt.art}_back`) : null) ?? artFrame(mt.art), mid = (mt.w * TILE) / 2;
    propShadow(cx, mid, mid - 2);
    if (af) drawArt(cx, af, mid, 31 - lift);
    this.tileCache.set(key, c);
    return c;
  }

  /** v1.14.0: one tile of a doorway: the wall's cap band as a lintel, a post on each open end, a soft shadow under the lintel */
  private doorwayTile(left: boolean, right: boolean, closed = false): HTMLCanvasElement {
    // v1.18.1: a doorway is the plain wall (so its trim and skirting line up with its neighbours) with the drawn
    // doorway's opening cut out of it and its wooden frame laid on top. A one-tile door shows the whole piece, a wider
    // one its left half on the first tile, its right half on the last and the lintel over the middle tiles. `closed`:
    // the glass door stands in the opening (the office entrance, shut while nobody is near it)
    return this.padded(`doorway:${+left}${+right}:${+closed}`, (cx) => {
      const f = artFrame('wall:door'), hole = artFrame('wall:doorhole');
      drawArtWall(cx, 'face');
      if (!ART || !f) return;
      const [u0, u1] = left && right ? [0, 1] : left ? [0, 0.5] : right ? [0.5, 1] : [0.45, 0.55];
      const piece = (g: ArtFrame): void => { const sx = g.x + g.w * WALL_IN[0], sw = g.w * (WALL_IN[1] - WALL_IN[0]); cx.drawImage(ART!.img, sx + sw * u0, g.y, sw * (u1 - u0), g.h, 0, -16, TILE, TILE + 16); };
      if (hole) { cx.save(); cx.globalCompositeOperation = 'destination-out'; piece(hole); cx.restore(); }
      if (closed) {
        const gd = artFrame('obj:glassdoor');
        if (gd) { const w = TILE * 0.62, h = w * (gd.h / gd.w); cx.drawImage(ART.img, gd.x, gd.y, gd.w, gd.h, left && right ? (TILE - w) / 2 : left ? TILE - w * 0.98 : w * -0.02, 32 - h, w, h); }
      }
      piece(f);
    });
  }

  /** v1.6.0: one tile of a multi-tile sprite (sofa, reception, whiteboard): find the run this tile is in, pick its slice */
  private wideObjectTile(type: string, x: number, y: number): HTMLCanvasElement {
    const spec = WIDE_OBJECTS[type], f = artForObject(type);
    if (!f || !ART) return this.objectTile(type);
    const m = this.map;
    let x0 = x; while (this.typeAt(m.objects, x0 - 1, y) === type) x0--;
    let x1 = x; while (this.typeAt(m.objects, x1 + 1, y) === type) x1++;
    const n = x1 - x0 + 1, i = x - x0;
    if (n === 1 && spec.single) { const sf = artFrame(spec.single); if (sf) return this.padded(`art:obj:${spec.single}`, (cx) => drawArtProp(cx, sf)); }
    const slices = Math.max(1, Math.round(f.w / (TILE * ART.px)));
    const si = n === 1 ? -1 : i === 0 ? 0 : i === n - 1 ? slices - 1 : Math.min(slices - 1, Math.floor(slices / 2));
    return this.padded(`art:obj:${type}:${si}`, (cx) => drawArtSlice(cx, f, si, slices, spec.h));
  }

  /** a base furniture tile: from the drawn atlas when it has one, else procedural */
  private objectTile(type: string): HTMLCanvasElement {
    const af = artForObject(type);
    if (af && type in WALL_DECOR) return this.padded(`art:wall:${type}`, (cx) => drawArt(cx, af, 16, WALL_DECOR[type] + af.h / (ART?.px ?? 4) / 2));
    return this.padded(`art:obj:${type}`, (cx) => { if (af) drawArtProp(cx, af); });
  }

  private furniture(kind: PlaceKind, style: number, face: Facing): HTMLCanvasElement {
    const key = `${kind}:${style}:${face}`;
    let c = this.furnCache.get(key);
    if (c) return c;
    c = document.createElement('canvas');
    c.width = (TILE + TILE_PAD * 2) * TILE_SCALE; c.height = (TILE + TILE_PAD * 2) * TILE_SCALE;
    const ctx = c.getContext('2d')!;
    ctx.scale(TILE_SCALE, TILE_SCALE);
    ctx.translate(TILE_PAD, TILE_PAD);
    const af = artForFurniture(kind, style, face);
    if (kind !== 'platform') propShadow(ctx);
    strokeDefault = TOUT;
    if (af) drawArtFurniture(ctx, af, kind, style);
    strokeDefault = OUTLINE;
    this.furnCache.set(key, c);
    return c;
  }

  /** v0.17.0: the banner rect (world px) of each room, for double-click renaming. v1.16.0: a small name plate on the
   *  wall right beside the room's entrance (row = the wall row it hangs on, so whoever walks past covers it) */
  roomBanners(): { zone: string; x: number; y: number; w: number; h: number; row: number }[] {
    return this.map.zones.map((z) => {
      const bw = this.bannerWidth(z.name), m = this.map;
      // the doorway on the room's edge (rightmost tile of it), else the middle of the top wall as before
      let door: { x: number; y: number } | null = null;
      for (let y = z.y - 1; y <= z.y + z.h; y++) for (let x = z.x - 1; x <= z.x + z.w; x++) {
        const edge = y === z.y - 1 || y === z.y + z.h || x === z.x - 1 || x === z.x + z.w;
        if (edge && this.typeAt(m.floor, x, y) === 'door' && (!door || y > door.y || (y === door.y && x > door.x))) door = { x, y };
      }
      if (!door) return { zone: z.name, x: z.x * TILE + (z.w * TILE) / 2 - bw / 2, y: z.y * TILE - 12, w: bw, h: 14, row: z.y - 1 };
      return { zone: z.name, x: (door.x + 1) * TILE + 4, y: door.y * TILE + 1, w: bw, h: 14, row: door.y };
    });
  }

  /** world-px → tile, given a canvas-relative point */
  tileAt(cx: number, cy: number): { x: number; y: number; wx: number; wy: number } {
    const wx = this.camX + cx / this.zoom, wy = this.camY + cy / this.zoom;
    return { x: Math.floor(wx / TILE), y: Math.floor(wy / TILE), wx, wy };
  }

  private bannerLabel(zone: string): string { return `${(this.roomNames[zone] ?? zone).toUpperCase()}  ✎`; }
  private bannerWidth(zone: string): number {
    const ctx = this.ctx; ctx.font = BANNER_FONT;
    return ctx.measureText(this.bannerLabel(zone)).width + 12;
  }

  private spriteCache2 = new Map<string, { c: HTMLCanvasElement; w: number; h: number; fx: number; fy: number }>();
  /** v1.5.0: the sprite image plus its world-space size and the feet point inside it */
  private spriteFor(pose: Pose, look: Look, expr: Expr, facing: Facing): { c: HTMLCanvasElement; w: number; h: number; fx: number; fy: number; turned?: boolean } {
    const f = facing === 'left' ? 'right' : facing;
    const key = `${pose}|${f}|${expr}|${look.fur}|${look.top}|${look.topStyle}|${look.bottom}|${look.bottomStyle}|${look.hat}|${look.hatColor}|${look.eyes}`;
    const hit = this.spriteCache2.get(key);
    if (hit) return hit;
    const art = artOtter(pose, f, look, expr);
    let out: { c: HTMLCanvasElement; w: number; h: number; fx: number; fy: number; turned?: boolean };
    if (art) { const px = ART?.px ?? 4; out = { c: art.c, w: art.c.width / px, h: art.c.height / px, fx: (art.ax ?? art.c.width / 2) / px, fy: art.c.height / px, turned: art.turned }; }
    else { const c = document.createElement('canvas'); c.width = c.height = 1; out = { c, w: 0, h: 0, fx: 0, fy: 0 }; } // no atlas yet
    if (this.spriteCache2.size > 1500) this.spriteCache2.clear();
    this.spriteCache2.set(key, out);
    return out;
  }

  private typeAt(layer: number[], x: number, y: number): string | undefined {
    if (x < 0 || y < 0 || x >= this.map.width || y >= this.map.height) return undefined;
    return this.map.tileTypes.get(layer[y * this.map.width + x]);
  }

  /** Floors, rugs, flowers, room tint + banners: everything flat. */
  private buildStatic(): HTMLCanvasElement {
    const m = this.map;
    const S = TILE_SCALE;
    const c = document.createElement('canvas');
    c.width = m.width * TILE * S; c.height = m.height * TILE * S;
    const ctx = c.getContext('2d')!;
    ctx.scale(S, S);
    const P = TILE_PAD, TP = TILE + P * 2;
    const blit = (img: HTMLCanvasElement, x: number, y: number) => ctx.drawImage(img, x * TILE - P, y * TILE - P, TP, TP);
    for (let y = 0; y < m.height; y++) for (let x = 0; x < m.width; x++) {
      const type = this.typeAt(m.floor, x, y) ?? 'tile';
      // v1.14.0: a doorway shows the floor of the side that isn't corridor (the room it opens into)
      const ft = SOLID_PROP_TYPES.has(type) ? 'tile' : type === 'door' ? this.doorFloor(x, y) : type;
      const af = artFrame(`floor:${ft}`) ?? (FLOOR_STANDIN[ft] ? artFrame(`floor:${FLOOR_STANDIN[ft]}`) : null);
      if (af) drawArtTile(ctx, af, x, y);
    }
    // v1.4.0: ambient shade on the floor just below a wall face, and along the inside of side walls
    for (let y = 0; y < m.height; y++) for (let x = 0; x < m.width; x++) {
      const ft = this.typeAt(m.floor, x, y);
      if (!ft || !SOLID_PROP_TYPES.has(ft)) continue;
      const south = this.typeAt(m.floor, x, y + 1);
      if (south && !SOLID_PROP_TYPES.has(south)) { ctx.fillStyle = vgrad(ctx, (y + 1) * TILE, (y + 1) * TILE + 12, 'rgba(40,30,20,0.16)', 'rgba(40,30,20,0)'); ctx.fillRect(x * TILE, (y + 1) * TILE, TILE, 12); }
      const east = this.typeAt(m.floor, x + 1, y);
      if (east && !SOLID_PROP_TYPES.has(east)) { const g = ctx.createLinearGradient((x + 1) * TILE, 0, (x + 1) * TILE + 8, 0); g.addColorStop(0, 'rgba(40,30,20,0.08)'); g.addColorStop(1, 'rgba(40,30,20,0)'); ctx.fillStyle = g; ctx.fillRect((x + 1) * TILE, y * TILE, 8, TILE); }
    }
    for (let y = 0; y < m.height; y++) for (let x = 0; x < m.width; x++) {
      const type = this.typeAt(m.objects, x, y);
      if (type && FLAT_OBJECTS.has(type)) blit(this.objectTile(type), x, y);
    }
    for (const p of m.rugs.values()) blit(this.furniture(p.kind, p.style, p.face), p.x, p.y);
    // rooms: a soft tint (the banner is drawn live, above the walls)
    for (const z of m.zones) { ctx.fillStyle = 'rgba(255, 240, 210, 0.10)'; ctx.fillRect(z.x * TILE, z.y * TILE, z.w * TILE, z.h * TILE); }
    return c;
  }

  /** v1.14.0: the floor a doorway shows: the room's (inside a zone) on whichever side has one, else the tile north of it */
  private doorFloor(x: number, y: number): string {
    const inZone = (tx: number, ty: number): boolean => this.map.zones.some((z) => tx >= z.x && tx < z.x + z.w && ty >= z.y && ty < z.y + z.h);
    for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]] as const) if (inZone(x + dx, y + dy)) return this.typeAt(this.map.floor, x + dx, y + dy) ?? 'wood';
    return this.typeAt(this.map.floor, x, y - 1) ?? 'wood';
  }

  /** Room name plates (v1.16.0: drawn with the wall row they hang on). */
  private drawBanner(b: { zone: string; x: number; y: number; w: number; h: number }): void {
    const ctx = this.ctx;
    ctx.font = BANNER_FONT; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    rr(ctx, b.x, b.y, b.w, b.h, 4); fillStroke(ctx, '#1f5f6b', '#153f47', 1);
    ctx.fillStyle = '#e8fbff'; ctx.fillText(this.bannerLabel(b.zone), b.x + b.w / 2, b.y + b.h / 2 + 0.5);
  }

  /** Walls, furniture and placements as y-sorted props (walls rise above their tile). */
  private buildProps(): void {
    const m = this.map;
    const rows: Prop[][] = Array.from({ length: m.height }, () => []);
    for (let y = 0; y < m.height; y++) for (let x = 0; x < m.width; x++) {
      const ft = this.typeAt(m.floor, x, y);
      if (ft && SOLID_PROP_TYPES.has(ft)) {
        const south = this.typeAt(m.floor, x, y + 1);
        const open = !south || !SOLID_PROP_TYPES.has(south);
        const variant: WallVariant = ft === 'glass' ? (open ? 'glassface' : 'glass') : ft === 'window' && open ? 'window' : open ? 'face' : 'top';
        // v1.6.0: a framed picture on some plain wall faces
        // (v1.16.0: never within two tiles of a doorway, where the room's name plate hangs)
        const nearDoor = [-2, -1, 1, 2].some((d) => this.typeAt(m.floor, x + d, y) === 'door');
        const wv: WallVariant = variant === 'face' && !nearDoor && (x * 7 + y * 3) % 11 === 4 ? 'picture' : variant;
        rows[y].push({ x, y, img: this.wallTile(wv) });
      }
      const ot = this.typeAt(m.objects, x, y);
      const mt = ot ? MULTI_TILE[ot] : undefined;
      if (ot && mt) {
        // v1.16.0: a desk / table with chairs behind it (north, facing the viewer) is pulled up under their seats
        const cols = mt.dx < 0 ? [x] : Array.from({ length: mt.w }, (_, i) => x + i);
        const tucked = cols.some((sx) => m.seatFace.get(`${sx},${y - 1}`) === 'down');
        rows[y].push({ x: x + mt.dx, y, w: mt.w, img: this.multiTileObject(ot, tucked ? 7 : 0, tucked, (x * 31 + y * 17) % 3 === 1 ? '2' : '') });
      } else if (ot && !FLAT_OBJECTS.has(ot) && ot !== 'deskfill') rows[y].push({ x, y, img: ot in WIDE_OBJECTS ? this.wideObjectTile(ot, x, y) : this.objectTile(ot), seat: m.seats.has(`${x},${y}`) });
      // v1.14.0: a doorway is an opening in the wall: lintel + posts in the wall's own trim, the room's floor runs through
      if (ft === 'door') {
        const l = this.typeAt(m.floor, x - 1, y) !== 'door', r = this.typeAt(m.floor, x + 1, y) !== 'door';
        // v1.18.1: a door in the outer wall is the office entrance: a glass door that opens as someone comes near
        const entrance = y === m.height - 1 || y === 0 || x === 0 || x === m.width - 1;
        rows[y].push(entrance ? { x, y, img: this.doorwayTile(l, r, true), open: this.doorwayTile(l, r) } : { x, y, img: this.doorwayTile(l, r) });
      }
    }
    for (const p of m.placements.values()) if (p.kind !== 'platform') rows[p.y]?.push({ x: p.x, y: p.y, img: this.furniture(p.kind, p.style, p.face), seat: p.kind === 'chair' });
    for (const r of rows) r.sort((a, b) => a.x - b.x);
    this.rows = rows;
  }

  step(players: Iterable<RenderPlayer>, dt: number): void {
    const speed = 1 / MOVE_MIN_INTERVAL_MS; // exactly one tile per step interval
    for (const p of players) {
      const dx = p.x - p.rx, dy = p.y - p.ry;
      const dist = Math.hypot(dx, dy);
      if (dist > 8) { p.rx = p.x; p.ry = p.y; continue; }
      let step = speed * dt;
      if (dist > 1.25) step *= 1.5; // catch up gently if we've fallen more than a tile behind
      if (dist <= step) { p.rx = p.x; p.ry = p.y; continue; }
      p.rx += (dx / dist) * step;
      p.ry += (dy / dist) * step;
    }
  }

  draw(players: RenderPlayer[], me: RenderPlayer | null, desks: DeskMap): void {
    const ctx = this.ctx;
    const W = this.canvas.width, H = this.canvas.height;
    const s = this.zoom * this.dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#20242d'; ctx.fillRect(0, 0, W, H);

    const mapW = this.map.width * TILE, mapH = this.map.height * TILE;
    const viewW = W / s, viewH = H / s;
    if (me) { this.camX = (me.rx + 0.5) * TILE - viewW / 2; this.camY = (me.ry + 0.5) * TILE - viewH / 2; }
    this.camX = Math.max(0, Math.min(mapW - viewW, this.camX));
    this.camY = Math.max(0, Math.min(mapH - viewH, this.camY));
    if (viewW > mapW) this.camX = (mapW - viewW) / 2;
    if (viewH > mapH) this.camY = (mapH - viewH) / 2;

    ctx.setTransform(s, 0, 0, s, Math.round(-this.camX * s), Math.round(-this.camY * s));
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(this.staticLayer, 0, 0, mapW, mapH);
    this.drawDeskPlates(desks);
    if (this.ghost && this.ghost.kind === 'platform') this.drawGhost();

    const now = performance.now();
    const P = TILE_PAD, TP = TILE + P * 2;
    const x0 = Math.floor(this.camX / TILE) - 1, x1 = Math.ceil((this.camX + viewW) / TILE) + 1;
    const y0 = Math.max(0, Math.floor(this.camY / TILE) - 1), y1 = Math.min(this.map.height - 1, Math.ceil((this.camY + viewH) / TILE) + 2);
    // players bucketed by the row their feet are on
    const byRow = new Map<number, RenderPlayer[]>();
    for (const p of players) { const r = Math.floor(p.ry + 0.5); (byRow.get(r) ?? byRow.set(r, []).get(r)!).push(p); }
    const plates = this.roomBanners();
    const tops = new Map<RenderPlayer, number>();
    for (let y = y0; y <= y1; y++) {
      for (const pr of this.rows[y] ?? []) {
        if (pr.x + (pr.w ?? 1) < x0 || pr.x > x1) continue;
        // an entrance door stands open while anyone is within a tile and a half of it
        const img = pr.open && players.some((p) => Math.abs(p.rx - pr.x) < 1.6 && Math.abs(p.ry - pr.y) < 1.6) ? pr.open : pr.img;
        ctx.drawImage(img, pr.x * TILE - P, pr.y * TILE - P, (pr.w ?? 1) * TILE + P * 2, TP);
      }
      for (const b of plates) if (b.row === y) this.drawBanner(b);
      const ps = byRow.get(y);
      if (ps) for (const p of ps.sort((a, b) => a.rx - b.rx || (a === me ? 1 : -1))) {
        const top = this.drawAvatar(p, p === me, now);
        tops.set(p, top);
        // v1.14.0: sitting with your back to the viewer, the chair / sofa back is between you and them: draw it again on
        // top -- below the shoulders only, so a high sofa back can't swallow the whole otter
        if (p.sit && Math.abs(p.rx - p.x) < 0.01 && Math.abs(p.ry - p.y) < 0.01 && this.map.seatFace.get(`${p.x},${p.y}`) === 'up') {
          const seat = (this.rows[y] ?? []).find((pr) => pr.x === p.x && pr.seat);
          if (seat) {
            // (a sofa back is drawn much higher than a chair's, so over a sofa only the lower 40% is covered)
            const feet = p.y * TILE + 30, cut = this.map.seats.get(`${p.x},${p.y}`) === 'sofa' ? feet - (feet - top) * 0.4 : top + (feet - top) * 0.42;
            ctx.save(); ctx.beginPath(); ctx.rect(seat.x * TILE - P, cut, TP, seat.y * TILE + TILE + P - cut); ctx.clip();
            ctx.drawImage(seat.img, seat.x * TILE - P, seat.y * TILE - P, TP, TP);
            ctx.restore();
          }
        }
      }
    }
    // v1.18.1: name tags go on last, so a desk or wall in a later row never covers a seated otter's name
    for (const p of players) this.drawNameTag(p);
    // v1.20.0: speech bubbles above everything, newest on top
    for (const p of players.filter((q) => q.say && now - q.say.start < SAY_MS).sort((a, b) => a.say!.start - b.say!.start)) this.drawBubble(p, tops.get(p) ?? p.ry * TILE - 20, now);
    if (this.ghost && this.ghost.kind !== 'platform') this.drawGhost();
  }

  private drawBubble(p: RenderPlayer, top: number, now: number): void {
    const ctx = this.ctx, say = p.say!, age = now - say.start;
    const alpha = Math.min(1, age / 150, (SAY_MS - age) / 400);
    ctx.save(); ctx.globalAlpha = Math.max(0, alpha);
    ctx.font = '10px system-ui'; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
    // wrap to at most 3 lines of ~130px, ellipsis on the last
    const MAXW = 130, words = say.text.replace(/\s+/g, ' ').split(' '), lines: string[] = [];
    let line = '';
    for (const w of words) {
      const next = line ? `${line} ${w}` : w;
      if (ctx.measureText(next).width <= MAXW) { line = next; continue; }
      if (line) lines.push(line);
      line = w;
      while (ctx.measureText(line).width > MAXW) { let i = line.length - 1; while (i > 1 && ctx.measureText(line.slice(0, i)).width > MAXW) i--; lines.push(line.slice(0, i)); line = line.slice(i); }
    }
    if (line) lines.push(line);
    if (lines.length > 3) { lines.length = 3; let l = lines[2]; while (l.length > 1 && ctx.measureText(`${l}…`).width > MAXW) l = l.slice(0, -1); lines[2] = `${l}…`; }
    const w = Math.max(...lines.map((l) => ctx.measureText(l).width)) + 12, h = lines.length * 12 + 8;
    const cx = p.rx * TILE + 16, x = cx - w / 2, y = top - h - 7;
    ctx.strokeStyle = 'rgba(120,84,52,0.55)'; ctx.lineWidth = 1; ctx.fillStyle = '#fffaf0';
    rr(ctx, x, y, w, h, 6); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(cx - 4, y + h - 0.5); ctx.lineTo(cx, y + h + 5); ctx.lineTo(cx + 4, y + h - 0.5); ctx.fill();
    ctx.beginPath(); ctx.moveTo(cx - 4, y + h); ctx.lineTo(cx, y + h + 5); ctx.lineTo(cx + 4, y + h); ctx.stroke();
    ctx.fillStyle = '#3b2a20';
    lines.forEach((l, i) => ctx.fillText(l, x + 6, y + 4 + i * 12));
    ctx.restore();
  }

  private drawNameTag(p: RenderPlayer): void {
    const ctx = this.ctx, label = p.name;
    ctx.font = '11px system-ui'; ctx.textAlign = 'center';
    const w = ctx.measureText(label).width + 10;
    const tagY = p.ry * TILE + 33;
    rr(ctx, p.rx * TILE + 16 - w / 2, tagY, w, 14, 5); ctx.fillStyle = 'rgba(30,30,40,0.75)'; ctx.fill();
    ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
    ctx.fillText(label, p.rx * TILE + 16, tagY + 7);
  }

  private drawGhost(): void {
    const g = this.ghost!, ctx = this.ctx, P = TILE_PAD;
    ctx.globalAlpha = 0.65;
    ctx.drawImage(this.furniture(g.kind, g.style, g.face), g.x * TILE - P, g.y * TILE - P, TILE + P * 2, TILE + P * 2);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = g.ok ? '#4ade80' : '#f87171'; ctx.lineWidth = 1.5; ctx.setLineDash([3, 3]);
    ctx.strokeRect(g.x * TILE + 0.75, g.y * TILE + 0.75, TILE - 1.5, TILE - 1.5); ctx.setLineDash([]);
  }

  private drawDeskPlates(desks: DeskMap): void {
    const ctx = this.ctx;
    for (const [id, pos] of this.map.deskPos) {
      const owner = desks[id];
      // v1.21.0: only claimed desks get a plate (no more "free"), and the name on it is at most 8 characters
      if (!owner) continue;
      const label = owner.name.length > 8 ? `${owner.name.slice(0, 7)}…` : owner.name;
      // v1.4.0: the plate sits on the chair's free side — above a chair that faces down, below one that faces up
      const facing = this.map.seatFace.get(`${pos.x},${pos.y}`) ?? 'up';
      const x = pos.x * TILE + 16, y = facing === 'down' ? pos.y * TILE - 8 : pos.y * TILE + TILE + 6;
      ctx.font = 'bold 9px system-ui'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      const w = ctx.measureText(label).width + 10; // (8 characters always fit: no more clipping to the tile)
      rr(ctx, x - w / 2, y - 7, w, 14, 4); fillStroke(ctx, '#fff4d6', '#c98a1a', 1);
      ctx.fillStyle = '#3b2a20'; ctx.fillText(label, x, y);
    }
  }

  /** draws one otter with its overlays; returns the y of the top of its sprite */
  private drawAvatar(p: RenderPlayer, isMe: boolean, now: number): number {
    const ctx = this.ctx;
    let px = p.rx * TILE, py = p.ry * TILE;
    const interpolating = Math.abs(p.rx - p.x) > 0.01 || Math.abs(p.ry - p.y) > 0.01;
    // stay in the walk pose across the short gaps between grid steps
    const moving = interpolating || (p.movedAt !== undefined && now - p.movedAt < 200);
    const walkPose = (): Pose => `walk${Math.floor(((now % WALK_CYCLE_MS) / WALK_CYCLE_MS) * WALK_FRAMES) % WALK_FRAMES}`;

    if (p.sit && !interpolating) py -= SEAT_LIFT[this.map.seats.get(`${p.x},${p.y}`) ?? ''] ?? 0;

    let pose: Pose;
    if (p.sit) pose = 'sit';
    else if (moving) pose = walkPose();
    else if (p.pose === 'handsup') pose = 'handsup';
    else if (p.pose === 'flat') pose = 'flat';
    else pose = 'idle';

    const look = lookOf(p.id, p.avatar);
    let expr: Expr = look.expr;
    let t = -1;
    if (p.emote) {
      const dur = p.emote.kind === 'jump' ? JUMP_MS : EMOTE_MS;
      t = (now - p.emote.start) / dur;
      if (t >= 1) { p.emote = undefined; t = -1; }
    }
    if (p.emote && t >= 0.22 && t < 0.97 && p.emote.kind === 'jump' && !p.sit) pose = 'jump';
    if (p.emote && t >= 0) {
      expr = p.emote.kind === 'jump' ? look.expr : EMOTE_EXPR[p.emote.kind];
      switch (p.emote.kind) {
        case 'dance': px += Math.round(Math.sin(now / 90) * 3); if (pose === 'idle') pose = walkPose(); break;
        // v1.13.0: standing still, the otter waves its drawn paw (two frames per view, with its top / headwear / mood)
        case 'wave': if (pose === 'idle' && artFrame('otter:wave0:down')) pose = `wave${Math.floor(now / WAVE_FRAME_MS) % 2}`; break;
        case 'jump': if (!p.sit) py -= Math.round(Math.sin(t * Math.PI) * 22); break;
        case 'laugh': px += Math.round(Math.sin(now / 40) * 1.2); break;
        case 'angry': px += Math.round(Math.sin(now / 30) * 0.8); break;
        default: break;
      }
    }
    if (pose === 'idle' || pose === 'sit') py += Math.sin(now / 650 + p.id.length) * 0.7;

    const k = AVATAR_SCALE;
    ctx.fillStyle = 'rgba(0,0,0,0.18)';
    ellipse(ctx, p.rx * TILE + 16, p.ry * TILE + 30.5, 11 * k, 3.5 * k); ctx.fill();

    // a chair turns you to face the way it faces; while sitting on a sofa etc. keep your own facing
    const facing: Facing = p.sit ? (this.map.seatFace.get(`${p.x},${p.y}`) ?? p.face) : p.face;
    const sp = this.spriteFor(pose, look, expr, facing);
    const ax = px + 16, ay = py + 30; // feet
    const spriteY = ay - Math.min(sp.fy, 50) * k + 4; // "head top" reference for the overlays below
    ctx.save();
    ctx.translate(ax, ay); ctx.scale(k, k);
    // v1.7.0: the jump mirrors too; v1.11.0: so do hands up / flat once they use the drawn side frame
    if (facing === 'left' && ((pose !== 'handsup' && pose !== 'flat') || sp.turned)) ctx.scale(-1, 1);
    ctx.drawImage(sp.c, -sp.fx, -sp.fy, sp.w, sp.h);
    ctx.restore();

    // v1.17.0: emote sparkles are drawn pieces too (art-46 fx sheet); until it's in the atlas there are none
    const fxHeart = artFrame('fx:heart'), fxStar = artFrame('fx:sparkle');
    if (p.emote && t >= 0 && p.emote.kind === 'heart') {
      for (let i = 0; i < 3; i++) {
        const ti = (t + i * 0.3) % 1;
        ctx.globalAlpha = 1 - ti;
        if (fxHeart) drawArt(ctx, fxHeart, px + 16 + Math.sin((ti + i) * 5) * 10, spriteY + 2 - ti * 30, 0.5);
      }
      ctx.globalAlpha = 1;
    }
    if (p.emote && t >= 0 && (p.emote.kind === 'dance' || p.emote.kind === 'wave')) {
      ctx.globalAlpha = 0.7 + Math.sin(now / 80) * 0.3;
      if (fxStar) { drawArt(ctx, fxStar, px + 30, spriteY + 5 + Math.sin(now / 150) * 3, 0.5); drawArt(ctx, fxStar, px + 2, spriteY + 12 + Math.cos(now / 170) * 3, 0.35); }
      ctx.globalAlpha = 1;
    }

    if (p.speaking) { ctx.strokeStyle = '#4ade80'; ctx.lineWidth = 2; rr(ctx, px + 16 - 13 * k, spriteY - 2, 26 * k, 50 * k, 8 * k); ctx.stroke(); }
    if (isMe) { ctx.beginPath(); ctx.moveTo(px + 16, spriteY - 6); ctx.lineTo(px + 12, spriteY - 12); ctx.lineTo(px + 20, spriteY - 12); ctx.closePath(); fillStroke(ctx, '#ffffff', 'rgba(0,0,0,0.35)', 1); }
    if (p.mic) { ellipse(ctx, px + 29, spriteY + 4, 3.2, 3.2); fillStroke(ctx, '#4ade80', '#166534', 1); }
    if (p.cam) {
      // v0.19.0: the little screen shows the actual camera feed
      const w = 30, h = 20, mx = px + 16, my = spriteY - (isMe ? 26 : 16);
      const v = this.videoFor?.(p.id) ?? null;
      rr(ctx, mx - w / 2 - 1.5, my - h / 2 - 1.5, w + 3, h + 3, 3); fillStroke(ctx, '#2e3340', '#0f1218', 1);
      if (v && v.readyState >= 2 && v.videoWidth > 0) {
        ctx.save(); rr(ctx, mx - w / 2, my - h / 2, w, h, 2); ctx.clip();
        let sw = v.videoWidth, sh = v.videoHeight;
        if (sw / sh > w / h) sw = sh * (w / h); else sh = sw / (w / h);
        const sx = (v.videoWidth - sw) / 2, sy = (v.videoHeight - sh) / 2;
        if (isMe) { ctx.translate(mx + w / 2, my - h / 2); ctx.scale(-1, 1); ctx.drawImage(v, sx, sy, sw, sh, 0, 0, w, h); }
        else ctx.drawImage(v, sx, sy, sw, sh, mx - w / 2, my - h / 2, w, h);
        ctx.restore();
      } else {
        rr(ctx, mx - w / 2, my - h / 2, w, h, 2); ctx.fillStyle = vgrad(ctx, my - h / 2, my + h / 2, '#7cc8ff', '#3f8fe0'); ctx.fill();
      }
      ctx.fillStyle = '#2e3340'; ctx.fillRect(mx - 1.5, my + h / 2 + 1.5, 3, 2.5); ctx.fillRect(mx - 4, my + h / 2 + 4, 8, 1.5);
      ellipse(ctx, mx + w / 2 - 3, my - h / 2 + 3, 1.6, 1.6); fillStroke(ctx, p.screen ? '#f0932b' : '#4ade80', '#0f1218', 0.7);
    }

    return ay - sp.fy * k;
  }

  static dirDelta(d: Dir): [number, number] {
    return d === 'up' ? [0, -1] : d === 'down' ? [0, 1] : d === 'left' ? [-1, 0] : [1, 0];
  }
}
